import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Field } from "../components/MemorialFields";
import { Checkbox } from "../components/ui/checkbox";

const CATS = ["general", "support", "deletion", "report", "other"];

export default function ContactPage() {
  const { t } = useTranslation();
  const { lang, settings } = useSite();
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "", category: "general", consent: false, website: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error(t("errors.consent_required"));
    setBusy(true);
    try {
      await api.post("/contact", { ...f, lang });
      setSent(true);
    } catch (err) {
      toast.error(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main id="main" className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-24 grid lg:grid-cols-12 gap-12" data-testid="contact-page">
      <div className="lg:col-span-5">
        <h1 className="font-heading text-4xl sm:text-5xl">{t("contact.title")}</h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-5 leading-relaxed">{t("contact.lead")}</p>
        {settings.contact_email && <a href={`mailto:${settings.contact_email}`} className="block mt-6 font-semibold">{settings.contact_email}</a>}
      </div>
      <div className="lg:col-span-7">
        {sent || settings.features?.contact === false ? (
          <div className="rounded-3xl border bg-[hsl(var(--card))] p-10" data-testid="contact-sent">
            <h2 className="font-heading text-3xl">{sent ? t("contact.sent_title") : t("contact.title")}</h2>
            <p className="mt-4 text-[hsl(var(--muted-foreground))]">{sent ? t("contact.sent_text") : t("errors.feature_disabled")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl border bg-[hsl(var(--card))] p-6 sm:p-10 grid gap-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field id="c-name" label={t("contact.name")}><input id="c-name" required maxLength={80} className="field-input" value={f.name} onChange={set("name")} data-testid="contact-name-input" /></Field>
              <Field id="c-email" label={t("contact.email")}><input id="c-email" required type="email" className="field-input" value={f.email} onChange={set("email")} data-testid="contact-email-input" /></Field>
            </div>
            <Field id="c-cat" label={t("contact.category")}>
              <select id="c-cat" className="field-input" value={f.category} onChange={set("category")} data-testid="contact-category-select">
                {CATS.map((c) => <option key={c} value={c}>{t(`contact.categories.${c}`)}</option>)}
              </select>
            </Field>
            <Field id="c-subject" label={t("contact.subject")}><input id="c-subject" required maxLength={150} className="field-input" value={f.subject} onChange={set("subject")} data-testid="contact-subject-input" /></Field>
            <Field id="c-msg" label={t("contact.message")}><textarea id="c-msg" required maxLength={4000} className="field-input min-h-[160px]" value={f.message} onChange={set("message")} data-testid="contact-message-input" /></Field>
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={set("website")} />
            <label className="flex gap-3 items-start text-sm">
              <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} className="mt-0.5" data-testid="contact-consent-checkbox" />
              <span>{t("contact.consent")} <Link to="/privacy" className="underline">{t("footer.privacy")}</Link></span>
            </label>
            <button className="btn-primary justify-self-start" disabled={busy} data-testid="contact-submit-btn">{t("contact.send")}</button>
          </form>
        )}
      </div>
    </main>
  );
}
